import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Title, useNotify } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchConversationThread, generateAssistantReply, sendManualReply } from './messageInboxApi';
import { AssistantTurnResponse, MessageThread, TwilioMessage } from '../types/messageInbox';
import useAuthorize from '../hooks/useAuthorize';
import { WHATSAPP_MODULE_ROLES } from '../roles/portalRoles';

const MessageBubble = ({ message }: { message: TwilioMessage }) => {
  const isInbound = message.direction === 'inbound';
  const payload = message.raw_payload;
  const mediaUrl = payload?.mediaUrl || payload?.MediaUrl0 || null;
  const contentType = payload?.MediaContentType0 || '';

  const isImage = mediaUrl && (
    contentType.startsWith('image/') || 
    /\.(jpg|jpeg|png|gif|webp)/i.test(mediaUrl)
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isInbound ? 'flex-start' : 'flex-end',
        mb: 1.5,
      }}
    >
      <Paper
        sx={{
          p: 1.5,
          maxWidth: '75%',
          bgcolor: isInbound ? 'grey.100' : 'primary.light',
          color: isInbound ? 'text.primary' : 'primary.contrastText',
        }}
      >
        {mediaUrl && isImage && (
          <Box sx={{ mb: 1, maxHeight: 240, overflow: 'hidden', borderRadius: 1 }}>
            <img 
              src={mediaUrl} 
              alt="Attachment" 
              style={{ maxWidth: '100%', maxHeight: 240, display: 'block', cursor: 'pointer', borderRadius: 4 }}
              onClick={() => window.open(mediaUrl, '_blank')}
            />
          </Box>
        )}
        
        {mediaUrl && !isImage && (
          <Box sx={{ mb: 1 }}>
            <Button
              variant="contained"
              size="small"
              href={mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              color={isInbound ? 'inherit' : 'secondary'}
              sx={{ textTransform: 'none' }}
            >
              View Document
            </Button>
          </Box>
        )}

        {message.body && (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.body}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip label={message.status} size="small" />
          {message.replied_by && <Chip label={`by ${message.replied_by}`} size="small" variant="outlined" />}
          <Typography variant="caption">
            {new Date(message.dt_created).toLocaleString()}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

const MessageInboxShow = () => {
  useAuthorize(WHATSAPP_MODULE_ROLES);
  const { conversationKey = '' } = useParams();
  const notify = useNotify();
  const navigate = useNavigate();
  const [thread, setThread] = useState<MessageThread | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [assistantSuggestion, setAssistantSuggestion] = useState<AssistantTurnResponse | null>(null);
  const [sending, setSending] = useState(false);
  const [generatingSuggestion, setGeneratingSuggestion] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadThread = async () => {
    setLoading(true);
    try {
      const data = await fetchConversationThread(conversationKey);
      setThread(data);
      setAssistantSuggestion(null);
    } catch (error) {
      console.error(error);
      notify('Failed to load conversation', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (conversationKey) {
      loadThread();
    }
  }, [conversationKey]);

  const handleSendReply = async () => {
    if (!replyBody.trim() && !attachmentFile) {
      notify('Enter a reply message or attach a file', { type: 'warning' });
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('body', replyBody.trim());
      if (attachmentFile) {
        formData.append('attachment', attachmentFile);
      }

      await sendManualReply(conversationKey, formData);
      setReplyBody('');
      setAttachmentFile(null);
      notify('Reply sent', { type: 'success' });
      await loadThread();
    } catch (error: any) {
      console.error(error);
      notify(error.response?.data?.error || 'Failed to send reply', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleGenerateAssistantReply = async () => {
    if (!thread) return;
    const hasInbound = thread.messages.some((message) => message.direction === 'inbound' && message.body);
    if (!hasInbound) {
      notify('No inbound message found for AI suggestion', { type: 'warning' });
      return;
    }

    setGeneratingSuggestion(true);
    try {
      const suggestion = await generateAssistantReply(thread);
      setAssistantSuggestion(suggestion);
      notify('AI suggestion generated', { type: 'info' });
    } catch (error: any) {
      console.error(error);
      notify(error.response?.data?.error || 'Failed to generate AI suggestion', { type: 'error' });
    } finally {
      setGeneratingSuggestion(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/messageinbox')} sx={{ mb: 2 }}>
        Back to inbox
      </Button>

      <Title title={`Conversation ${thread?.contact_phone || conversationKey}`} />

      {loading ? (
        <Typography>Loading conversation...</Typography>
      ) : !thread ? (
        <Typography>Conversation not found.</Typography>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">{thread.contact_name || 'Unknown contact'}</Typography>
            <Typography color="text.secondary">{thread.contact_phone}</Typography>
            {thread.customer_id ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  {thread.customer_name || thread.contact_name || 'Customer'} · ID{' '}
                  <a
                    href={`${process.env.REACT_APP_ADMIN_URL || 'http://localhost:3000'}/#/customers/${thread.customer_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {thread.customer_id}
                  </a>
                  {thread.company_name ? ` · ${thread.company_name}` : ''}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No linked Credco customer for this phone yet.
              </Typography>
            )}
            {thread.product_context && thread.product_context.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Watchlist</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {thread.product_context.map((item) => (
                    <Chip
                      key={item.product_id}
                      label={[
                        item.category,
                        item.subcategory,
                        item.product_name,
                        item.product_grade,
                        item.product_mfi ? `${item.product_mfi} MFI` : null,
                      ].filter(Boolean).join(' · ')}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 2, mb: 2, minHeight: 360, maxHeight: 520, overflowY: 'auto' }}>
            {thread.messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="subtitle1">Manual reply</Typography>
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                disabled={generatingSuggestion}
                onClick={handleGenerateAssistantReply}
              >
                {generatingSuggestion ? 'Generating...' : 'AI suggestion'}
              </Button>
            </Box>

            {assistantSuggestion && (
              <Box sx={{ p: 2, mb: 2, bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip label={`Intent: ${assistantSuggestion.intent}`} size="small" />
                  <Chip
                    label={`Confidence: ${Math.round((assistantSuggestion.confidence || 0) * 100)}%`}
                    size="small"
                    color={assistantSuggestion.handoff_reason ? 'warning' : 'default'}
                  />
                  {assistantSuggestion.action?.type && (
                    <Chip
                      label={`${assistantSuggestion.action.type}: ${assistantSuggestion.action.status || 'suggested'}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                  {assistantSuggestion.reply_text}
                </Typography>
                {assistantSuggestion.handoff_reason && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
                    Handoff: {assistantSuggestion.handoff_reason}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setReplyBody(assistantSuggestion.reply_text)}
                  >
                    Use suggestion
                  </Button>
                </Box>
              </Box>
            )}

            <TextField
              multiline
              minRows={4}
              fullWidth
              placeholder="Type your WhatsApp reply..."
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
              >
                Attach File
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setAttachmentFile(e.target.files[0]);
                    }
                  }}
                />
              </Button>
              {attachmentFile && (
                <Chip
                  label={attachmentFile.name}
                  onDelete={() => setAttachmentFile(null)}
                  color="primary"
                  variant="outlined"
                />
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                disabled={sending || (!replyBody.trim() && !attachmentFile)}
                onClick={handleSendReply}
              >
                {sending ? 'Sending...' : 'Send reply'}
              </Button>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MessageInboxShow;
